import { useCountdown } from "@/lib/hooks";
import { Link } from "react-router-dom";
import { MapPin, Calendar, Clock } from "lucide-react";

const WEDDING_DATE = new Date("2027-06-04T13:00:00+01:00");

function Cell({ label, value, testid }) {
  return (
    <div
      data-testid={testid}
      className="flex flex-col items-center justify-center min-w-[64px]"
    >
      <span className="font-display text-3xl md:text-4xl text-[#1A1818] font-light tabular-nums">
        {String(value).padStart(2, "0")}
      </span>
      <span className="text-[10px] md:text-xs tracking-luxe uppercase text-[#4A4747] mt-1">
        {label}
      </span>
    </div>
  );
}

export default function CommandCenter() {
  const { days, hours, minutes, seconds } = useCountdown(WEDDING_DATE);

  return (
    <section
      data-testid="command-center"
      className="relative z-20 max-w-6xl mx-auto px-6 -mt-20 md:-mt-24"
    >
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
        {/* When card */}
        <div className="bg-[#FAF9F6] border border-[#6A1E2D]/10 p-8 md:p-10 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.25)] flex flex-col items-center text-center">
          <Calendar className="w-5 h-5 text-[#6A1E2D] mb-3" strokeWidth={1.4} />
          <p className="text-[10px] tracking-luxe uppercase text-[#6A1E2D] mb-2">When</p>
          <p className="font-display text-2xl md:text-3xl text-[#1A1818] leading-tight">
            Friday, 4 June 2027
          </p>
          <p className="text-sm text-[#4A4747] mt-2">Ceremony at 1:00pm</p>
        </div>

        {/* Countdown card */}
        <div
          data-testid="countdown-card"
          className="bg-[#6A1E2D] p-8 md:p-10 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.35)] flex flex-col items-center text-center text-[#FAF9F6]"
        >
          <Clock className="w-5 h-5 text-[#A3C1DA] mb-3" strokeWidth={1.4} />
          <p className="text-[10px] tracking-luxe uppercase text-[#A3C1DA] mb-4">The countdown</p>
          <div className="flex items-end justify-center gap-4 md:gap-5 text-[#FAF9F6]">
            <div className="flex flex-col items-center min-w-[56px]">
              <span data-testid="countdown-days" className="font-display text-3xl md:text-4xl tabular-nums">{days}</span>
              <span className="text-[9px] tracking-luxe uppercase mt-1 text-[#FAF9F6]/70">Days</span>
            </div>
            <div className="flex flex-col items-center min-w-[44px]">
              <span data-testid="countdown-hours" className="font-display text-3xl md:text-4xl tabular-nums">{String(hours).padStart(2,"0")}</span>
              <span className="text-[9px] tracking-luxe uppercase mt-1 text-[#FAF9F6]/70">Hrs</span>
            </div>
            <div className="flex flex-col items-center min-w-[44px]">
              <span data-testid="countdown-minutes" className="font-display text-3xl md:text-4xl tabular-nums">{String(minutes).padStart(2,"0")}</span>
              <span className="text-[9px] tracking-luxe uppercase mt-1 text-[#FAF9F6]/70">Min</span>
            </div>
            <div className="flex flex-col items-center min-w-[44px]">
              <span data-testid="countdown-seconds" className="font-display text-3xl md:text-4xl tabular-nums">{String(seconds).padStart(2,"0")}</span>
              <span className="text-[9px] tracking-luxe uppercase mt-1 text-[#FAF9F6]/70">Sec</span>
            </div>
          </div>
        </div>

        {/* Where card */}
        <div className="bg-[#FAF9F6] border border-[#6A1E2D]/10 p-6 md:p-8 shadow-[0_18px_40px_-18px_rgba(0,0,0,0.25)] flex flex-col items-center text-center">
          <MapPin className="w-5 h-5 text-[#6A1E2D] mb-3" strokeWidth={1.4} />
          <p className="text-[10px] tracking-luxe uppercase text-[#6A1E2D] mb-3">Where</p>
          <a
            href="https://www.google.com/maps/search/?api=1&query=St.+Joseph%27s+Church+Presentation+Road+Galway"
            target="_blank"
            rel="noreferrer noopener"
            data-testid="church-map-link"
            className="block group"
          >
            <p className="text-[9px] tracking-luxe uppercase text-[#4A4747] mb-1">Ceremony</p>
            <p className="font-display text-xl md:text-2xl text-[#1A1818] leading-tight group-hover:text-[#6A1E2D] transition-colors">
              St. Joseph's Church
            </p>
            <p className="text-xs text-[#4A4747] mt-1">Presentation Road, Galway</p>
            <span className="inline-block text-[10px] tracking-luxe uppercase text-[#6A1E2D] mt-2">
              Open in maps →
            </span>
          </a>

          <div className="w-10 h-px bg-[#6A1E2D]/20 my-4" />

          <a
            href="https://www.google.com/maps/search/?api=1&query=Glenlo+Abbey+Hotel+%26+Estate%2C+Bushy+Park%2C+Galway"
            target="_blank"
            rel="noreferrer noopener"
            data-testid="venue-map-link"
            className="block group"
          >
            <p className="text-[9px] tracking-luxe uppercase text-[#4A4747] mb-1">Reception</p>
            <p className="font-display text-xl md:text-2xl text-[#1A1818] leading-tight group-hover:text-[#6A1E2D] transition-colors">
              Glenlo Abbey
            </p>
            <p className="text-xs text-[#4A4747] mt-1">Bushy Park, Galway</p>
            <span className="inline-block text-[10px] tracking-luxe uppercase text-[#6A1E2D] mt-2">
              Open in maps →
            </span>
          </a>
        </div>
      </div>

      <div className="text-center mt-10">
        <Link
          to="/rsvp"
          data-testid="cta-rsvp-anchor"
          className="inline-flex items-center gap-3 bg-[#6A1E2D] hover:bg-[#521723] text-[#FAF9F6] px-10 py-4 text-xs tracking-luxe uppercase transition-colors duration-300"
        >
          Reply to our invitation
          <span aria-hidden>→</span>
        </Link>
      </div>
    </section>
  );
}
